import React, { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Input, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, LoadingButton } from '@redpanda/ui';
import { createFiscalYear, updateFiscalYear } from '../services/api';
import { FiscalYear, useFiscalYearStoreBase, utcDateInput } from '../stores/periodStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';

interface FiscalYearDialogProps {
  /** 有值＝編輯；沒有＝新增 */
  fiscalYear?: FiscalYear;
  /** 新增時預設的年度（例：今年） */
  defaultYear?: number;
  onClose: () => void;
  onSaved?: (fiscalYear: FiscalYear) => void;
}

interface FormData {
  name: string;
  startDate: string;
  endDate: string;
  description: string;
}

const yearForm = (year: number): FormData => ({ name: String(year), startDate: `${year}-01-01`, endDate: `${year}-12-31`, description: '' });

/**
 * 新增／編輯會計年度：建立時自動產生每個月的會計期間
 * 等伺服器回應才關：年度名稱重複、日期和其他年度重疊都由後端判斷
 */
const FiscalYearDialog: React.FC<FiscalYearDialogProps> = ({ fiscalYear, defaultYear = new Date().getFullYear(), onClose, onSaved }) => {
  const { t } = useTranslation();
  const isEditMode = !!fiscalYear;
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, reset, watch, setError, formState: { errors } } = useForm<FormData>({ defaultValues: yearForm(defaultYear) });

  useLayoutEffect(() => {
    reset(fiscalYear ? {
      name: fiscalYear.name,
      startDate: utcDateInput(fiscalYear.startDate),
      endDate: utcDateInput(fiscalYear.endDate),
      description: fiscalYear.description || '',
    } : yearForm(defaultYear));
  }, [fiscalYear, defaultYear, reset]);

  const onSubmit = async (data: FormData) => {
    const payload = { name: data.name.trim(), startDate: data.startDate, endDate: data.endDate, description: data.description.trim() };
    setSaving(true);
    try {
      const { data: saved } = fiscalYear ? await updateFiscalYear(fiscalYear.id, payload) : await createFiscalYear(payload);
      const store = useFiscalYearStoreBase.getState();
      if (fiscalYear) store.updateItem(fiscalYear.id, saved);
      else store.addItem(saved);
      toast.success(t(fiscalYear ? 'common.messages.updateSuccess' : 'periods.fiscalYearDialog.created', { count: saved.periods?.length ?? 0 }));
      notifyDataChanged('periods');
      onSaved?.(saved);
      onClose();
    } catch (error: any) {
      const code = error?.response?.data?.code;
      if (code === 'FISCAL_YEAR_NAME_EXISTS') setError('name', { type: 'manual', message: t('errors.FISCAL_YEAR_NAME_EXISTS') });
      else if (code === 'FISCAL_YEAR_OVERLAP') setError('startDate', { type: 'manual', message: t('errors.FISCAL_YEAR_OVERLAP') });
      else toast.error(apiErrorMessage(error, fiscalYear ? 'common.messages.updateFailed' : 'common.messages.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const required = { required: t('common.forms.validation.required') };

  return (
    <DialogShell onClose={onClose} closeDisabled={saving} className="max-w-lg">
      <DialogHeader
        title={isEditMode ? t('periods.fiscalYearDialog.editTitle') : t('periods.fiscalYearDialog.addTitle')}
        onClose={onClose}
        closeDisabled={saving}
        closeLabel={t('common.actions.close')}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-5">
          <FormFieldWithError label={t('periods.fiscalYearDialog.name')} required error={errors.name?.message} htmlFor="name">
            <Input id="name" autoFocus {...register('name', { validate: value => !!value.trim() || t('common.forms.validation.required') })} variant={errors.name ? 'error' : 'default'} />
          </FormFieldWithError>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormFieldWithError label={t('periods.fiscalYearDialog.startDate')} required error={errors.startDate?.message} htmlFor="startDate">
              <Input id="startDate" type="date" {...register('startDate', required)} variant={errors.startDate ? 'error' : 'default'} />
            </FormFieldWithError>
            <FormFieldWithError label={t('periods.fiscalYearDialog.endDate')} required error={errors.endDate?.message} htmlFor="endDate">
              <Input
                id="endDate"
                type="date"
                {...register('endDate', { ...required, validate: value => value > watch('startDate') || t('periods.fiscalYearDialog.endAfterStart') })}
                variant={errors.endDate ? 'error' : 'default'}
              />
            </FormFieldWithError>
          </div>
          <p className="text-[13px] text-fg-secondary -mt-2">{t('periods.fiscalYearDialog.periodsHint')}</p>
          <FormFieldWithError label={t('periods.fiscalYearDialog.description')} htmlFor="description">
            <Input id="description" {...register('description')} />
          </FormFieldWithError>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} disabled={saving} className={dialogButtonClass.cancel}>{t('common.actions.cancel')}</button>
          <LoadingButton type="submit" loading={saving} className={dialogButtonClass.primary}>
            {isEditMode ? t('common.actions.save') : t('common.actions.create')}
          </LoadingButton>
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

export default FiscalYearDialog;
